import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema, softwareApplicationSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, Star, Image, Zap, Layers, Download, Palette, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const AIBackgroundGenerator = () => {
  const faqs = [
    {
      question: "What types of backgrounds can I generate?",
      answer: "Generate any background imaginable: solid colors, gradients, textures, nature scenes, abstract patterns, studio setups, office environments, and more. Just describe what you need."
    },
    {
      question: "Are these backgrounds suitable for product photography?",
      answer: "Absolutely! Our AI backgrounds are perfect for e-commerce product photos, Amazon listings, and marketing materials. Generate professional white backgrounds, lifestyle scenes, or branded environments."
    },
    {
      question: "What resolutions are available?",
      answer: "Generate backgrounds in multiple resolutions up to 4K. Perfect for web, print, and large format displays. Enhanced DPI options available for print-ready quality."
    },
    {
      question: "Can I use these for video backgrounds?",
      answer: "Yes! Generate high-resolution backgrounds for video calls, YouTube thumbnails, streaming overlays, and video production. Consistent quality for any use case."
    },
    {
      question: "How do I remove or replace existing backgrounds?",
      answer: "Use our companion Background Remover tool to remove existing backgrounds, then composite with your AI-generated background. Perfect end-to-end workflow."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Any Style",
      description: "Abstract, realistic, textured, minimal - generate any background style."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Generation",
      description: "Create professional backgrounds in seconds, not hours."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Seamless Patterns",
      description: "Generate tileable patterns for web design and print."
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "High Resolution",
      description: "Up to 4K resolution. Print-ready quality with enhanced DPI."
    }
  ];

  const backgroundTypes = [
    "White/Studio",
    "Gradient",
    "Abstract",
    "Nature/Outdoor",
    "Office/Corporate",
    "Lifestyle Scenes",
    "Textures",
    "Patterns"
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="AI Background Generator | Create Stunning Backgrounds Instantly"
        description="Generate professional AI backgrounds for products, videos, and designs. White backgrounds, gradients, scenes, patterns. High resolution, instant delivery. Try free!"
        keywords="ai background generator, background maker ai, product background generator, white background ai, video background generator, ai backdrop creator, seamless pattern generator, texture generator ai"
        url="/ai-background-generator"
        jsonLd={[
          organizationSchema,
          softwareApplicationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'AI Background Generator', url: '/ai-background-generator' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                40,000+ Backgrounds Generated Monthly
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                AI Background Generator
                <span className="block text-primary mt-2">Any Background, Instantly</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning backgrounds for products, videos, and designs. 
                From white studio to abstract art. High resolution, instant delivery.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild size="lg" className="text-lg px-8">
                  <Link to="/try-generator">Generate Free Background</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> No signup required
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Up to 4K resolution
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Commercial rights
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Professional Backgrounds, Zero Effort</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Perfect backgrounds for any project, generated in seconds.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Background Types */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Every Background Type You Need</h2>
                <p className="text-muted-foreground">
                  From simple to complex, generate any background style
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {backgroundTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-2 bg-muted/50 p-4 rounded-lg">
                    <Palette className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              <div>
                <div className="text-4xl font-bold mb-2">40K+</div>
                <div className="text-primary-foreground/80">Backgrounds Created</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">4K</div>
                <div className="text-primary-foreground/80">Max Resolution</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;10s</div>
                <div className="text-primary-foreground/80">Generation Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">∞</div>
                <div className="text-primary-foreground/80">Style Options</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Perfect Backgrounds?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Stop searching stock sites. Generate exactly what you need in seconds.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Generate Backgrounds Free</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AIBackgroundGenerator;
